import { shuffle } from '../utilities'
import i18n from '../../../utilities/translation'
import { refreshVisibleAds } from '../utilities/ad_behaviors'
import ga4RevealWidgetClickHandler from '../ga4_support/blocks/reveal_widget'
import GA4Handler from '../ga4_support/GA4Handler'

const app_theme = document.documentElement.getAttribute('data-mp-app-theme')
const language = document.documentElement.lang

const BASE_CLICK_TEXT = i18n('Tap to reveal the quote.', language)
const BASE_REFRESH_TEXT = i18n('Tap to reset the quote.', language)

export const initQuotesDefault = (initialList, widgetContainers) => {
  let displayCount = 0
  let unseenContent = shuffle(initialList)

  // no teaser means no animation and no 'pre-slide' with author
  const initNoTeaser = (
    widgetContainer,
    widgetInnerContainer,
    quotesWidgetMessageContainer,
    activeRegion
  ) => {
    const revealQuote = ({ source, text }) => {
      quotesWidgetMessageContainer.innerHTML = `“${text}”<footer>${source}</footer>`
      widgetContainer.setAttribute('data-mp-share-quotes-text', text)
      widgetContainer.setAttribute('data-gtm-share-text', text)
    }

    const refreshContent = () => {
      if (!unseenContent.length) {
        unseenContent = shuffle(initialList)
      }
      activeQuote = unseenContent.pop()
      revealQuote(activeQuote)
      activeRegion.innerText =
        `${activeQuote.text} - ${activeQuote.source}` + '\n\n' + BASE_REFRESH_TEXT

      widgetContainer.setAttribute('data-gtm-feature', 'refresh')
      ga4RevealWidgetClickHandler(
        widgetContainer,
        'editorial',
        'refresh',
        activeQuote.source,
        activeQuote.text,
        ++displayCount
      )
      refreshVisibleAds()
    }

    widgetContainer.setAttribute('data-gtm-feature', 'show')
    widgetInnerContainer.removeAttribute('aria-label')
    widgetInnerContainer.setAttribute('aria-labelledby', 'quotes-widget__dialog-region')

    let activeQuote = unseenContent.pop()
    revealQuote(activeQuote)
    activeRegion.innerText =
      `${activeQuote.text} - ${activeQuote.source}` + '\n\n' + BASE_REFRESH_TEXT

    GA4Handler.impressionHandleEvent(widgetContainer as HTMLElement, {
      element_teaser: activeQuote.source,
      element_detail: activeQuote.text
    })

    widgetInnerContainer.addEventListener('click', refreshContent)
    widgetInnerContainer.addEventListener('keyup', (e: any) => {
      if (e.type !== 'keyup') {
        return
      }
      if (['Enter', ' '].includes((e as any).key)) {
        refreshContent()
      }
    })
  }

  const initWithTeaser = (
    widgetContainer,
    widgetInnerContainer,
    quotesWidgetMessageContainer,
    activeRegion
  ) => {
    let isRevealed = false

    const quotesWidgetItem = widgetContainer.querySelector('.quotes-widget')
    const actionButtons: any = widgetContainer.querySelector('.block-item__actions')
    const footer: any = widgetContainer.querySelector('.reveal-widget__footer')

    //set default gtm data attr fortune text when page loads
    widgetContainer.setAttribute('data-gtm-share-text', 'quote not revealed')

    const hideActionButtons = () => {
      actionButtons.setAttribute('tabindex', '-1')
      actionButtons.setAttribute('aria-hidden', 'true')
      actionButtons.classList.add('hidden')
    }

    const showActionButtons = () => {
      actionButtons.setAttribute('tabindex', '0')
      actionButtons.removeAttribute('aria-hidden')
      actionButtons.classList.remove('hidden')
    }

    const showMessage = () => {
      quotesWidgetItem.classList.remove('reveal-widget--hide-message')
      quotesWidgetItem.classList.add('reveal-widget--show-message')

      footer.classList.remove('reveal-widget--footer-disappear')
      footer.classList.add('reveal-widget--footer-appear')
      widgetContainer.setAttribute('data-gtm-feature', 'refresh')
      showActionButtons()
    }

    const hideMessage = () => {
      quotesWidgetItem.classList.remove('reveal-widget--show-message')
      quotesWidgetItem.classList.add('reveal-widget--hide-message')

      footer.classList.remove('reveal-widget--footer-appear')
      footer.classList.add('reveal-widget--footer-disappear')
      widgetContainer.setAttribute('data-gtm-feature', 'show')
      widgetContainer.setAttribute('data-gtm-share-text', 'quote not revealed')
      hideActionButtons()
    }

    const revealQuote = ({ source, text }) => {
      quotesWidgetMessageContainer.innerHTML = `“${text}”<footer>- ${source}</footer>`
      widgetContainer.setAttribute('data-mp-share-quotes-text', text)
      widgetContainer.setAttribute('data-gtm-share-text', text)
    }

    const revealTeaser = ({ source }) => {
      const message = `${source} ${i18n('once said', language)}...`
      quotesWidgetMessageContainer.innerHTML = message
      widgetContainer.setAttribute('data-mp-share-quotes-text', '')
      widgetContainer.setAttribute('data-gtm-feature', 'show')
    }

    let isClickedOnce = false
    let activeQuote = unseenContent.pop()
    revealTeaser(activeQuote)

    GA4Handler.impressionHandleEvent(widgetContainer as HTMLElement, {
      element_teaser: activeQuote.source,
      element_detail: activeQuote.text
    })

    const refreshContent = () => {
      if (!isClickedOnce) {
        widgetInnerContainer.removeAttribute('aria-label')
        widgetInnerContainer.setAttribute('aria-labelledby', 'quotes-widget__dialog-region')

        isClickedOnce = true
      }

      if (!isRevealed) {
        revealQuote(activeQuote)
        activeRegion.innerText =
          `${activeQuote.text} - ${activeQuote.source}` + '\n\n' + BASE_REFRESH_TEXT
        showMessage()
        ga4RevealWidgetClickHandler(
          widgetContainer,
          'editorial',
          'show',
          activeQuote.source,
          activeQuote.text,
          ++displayCount
        )
        refreshVisibleAds()
      } else {
        if (!unseenContent.length) {
          unseenContent = shuffle(initialList)
        }
        activeQuote = unseenContent.pop()
        activeRegion.innerText = `${activeQuote.source} ${i18n(
          'once said',
          language
        )}... \n\n ${BASE_CLICK_TEXT}`
        revealTeaser(activeQuote)
        hideMessage()

        ga4RevealWidgetClickHandler(widgetContainer, 'editorial', 'refresh', '', '')
      }

      isRevealed = !isRevealed
    }

    hideActionButtons()
    widgetInnerContainer.addEventListener('click', refreshContent)
    widgetInnerContainer.addEventListener('keyup', (e: any) => {
      if (e.type !== 'keyup') {
        return
      }
      if (['Enter', ' '].includes((e as any).key)) {
        refreshContent()
      }
    })
  }

  widgetContainers.forEach(widgetContainer => {
    const widgetInnerContainer = widgetContainer.querySelector('.reveal-widget__inner-container')

    // Init template
    const activeRegion: any = widgetContainer.querySelector('.reveal-widget__dialog-region')
    const quotesWidgetMessageContainer = widgetContainer.querySelector(
      '.reveal-widget__message > p'
    )

    if (app_theme === 'moment') {
      initNoTeaser(
        widgetContainer,
        widgetInnerContainer,
        quotesWidgetMessageContainer,
        activeRegion
      )
    } else {
      initWithTeaser(
        widgetContainer,
        widgetInnerContainer,
        quotesWidgetMessageContainer,
        activeRegion
      )
    }
  })
}
